<template>
  <CardHitosPago
    :viaje="storeHabitacion.getViaje"
    :precio-total="precioTotalGrupo"
    :total-pagado="totalPagadoGrupo"
    :hitos-pago="hitosPago"
    :is-pago-unico-forced="isPagoUnicoForced"
    :is-pagos-sucesivos="isPagosSucesivos"
    :fecha-vencimiento-hito-actual-already-payed="fechaVencimientoHitoActualAlreadyPayed"
    @pago-unico="handlePagoUnicoChanges"
  />
</template>

<script setup>
  import { ref, computed, onMounted, nextTick, watch } from 'vue';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import {
    calculaPrecioTotal,
    calculaPrecioTotalSinTasasInternas,
  } from '@/helpers/viajesGrupos';
  import { max } from '@popperjs/core/lib/utils/math';
  import { HITOS_PAGO_TIPOCANTIDAD } from '@/constants/viajeConstants';
  import currency from 'currency.js';
  import CardHitosPago from '@/components/card/viaje/CardHitosPago.vue';

  defineProps({
    isPagosSucesivos: {
      type: Boolean,
      required: false,
      default: false,
    },
    precioTotalGrupo: {
      type: Number,
      required: true,
    },
  });

  const emits = defineEmits(['pago-unico', 'precio-calendarizado']);

  const storeHabitacion = informacionHabitacion();
  const isPagoUnicoSelected = ref(true);
  const fechaVencimientoHitoActualAlreadyPayed = ref(null);

  const isPagoUnicoForced = computed(
    () =>
      storeHabitacion.getCodigoDescuento?.pagoUnico ||
      !storeHabitacion.getViaje?.pagoByCalendarioAllowed ||
      (storeHabitacion.getAlojamiento && storeHabitacion.getAlojamiento.habitacion.integrado)
  );

  const totalPagadoGrupo = computed(() =>
    storeHabitacion.getResumenReservas?.reduce((accum, reserva) => accum.add(calcularTotalPagado(reserva)), currency(0))
  );

  const hitosPago = computed(() => calculaHitosPago(storeHabitacion.getViaje?.hitosPago));

  function calcularTotalPagado(reserva) {
    return reserva?.pagos?.reduce((accum, pago) => accum.add(pago.cantidad), currency(0)) || currency(0);
  }

  function calcularTasasInternasPagadas(reserva) {
    return reserva?.pagos?.reduce((accum, pago) => accum.add(pago.fee), currency(0)) || currency(0);
  }

  function calculaHitosPago(hitosPago) {
    const fechaActual = Date.now();
    const repercutirFee = storeHabitacion.getViaje?.repercutirFee;
    const hitosPagoFinales = [];
    storeHabitacion.getResumenReservas.forEach(reserva => {
      const precioTotalSinTasasInternas = calculaPrecioTotalSinTasasInternas(reserva, false);
      const precioTotal = calculaPrecioTotal(reserva, false);
      let totalPagado = calcularTotalPagado(reserva);
      if (reserva.descuento) {
        totalPagado = totalPagado.add(reserva.descuento);
      }
      const totalTasasInternas = precioTotal.subtract(precioTotalSinTasasInternas);
      const totalTasasInternasPagadas = calcularTasasInternasPagadas(reserva);
      const totalPagadoSinTasasInternas = totalPagado.subtract(totalTasasInternasPagadas);
      let pagadoRestante = totalPagadoSinTasasInternas;
      let pagadoTasasInternasRestante = totalTasasInternasPagadas;

      let hitosPagoAjustados = ajustarHitosDePago(hitosPago, reserva);

      hitosPagoAjustados.forEach((hitoAjustado, idx) => {
        let cantidad = currency(max(0, hitoAjustado.cantidad.subtract(pagadoRestante).value));
        pagadoRestante = currency(max(0, pagadoRestante.subtract(hitoAjustado.cantidad).value));
        if (repercutirFee) {
          let ratio = hitoAjustado.cantidad.value / precioTotalSinTasasInternas.value;
          let cantidadTasas = max(0, totalTasasInternas.multiply(ratio).subtract(pagadoTasasInternasRestante).value);
          pagadoTasasInternasRestante = currency(
            max(0, pagadoTasasInternasRestante.subtract(totalTasasInternas.multiply(ratio)).value)
          );
          cantidad = cantidad.add(max(0, cantidadTasas));
        }
        hitoAjustado.cantidad = currency(max(0, cantidad.value));
        if (hitosPagoFinales[idx] == null) {
          hitosPagoFinales[idx] = hitoAjustado;
        } else {
          hitosPagoFinales[idx].cantidad = hitosPagoFinales[idx].cantidad.add(hitoAjustado.cantidad);
        }
      });
    });

    const hitosPagoPagados = hitosPagoFinales.filter(hito => hito.cantidad.value === 0);
    if (hitosPagoPagados.length > 0) {
      const lastHitoPagoPagado = hitosPagoPagados[hitosPagoPagados.length - 1];
      if (new Date(lastHitoPagoPagado.fecha) >= fechaActual) {
        fechaVencimientoHitoActualAlreadyPayed.value = lastHitoPagoPagado.fecha;
      }
    }
    return hitosPagoFinales.filter(hito => hito.cantidad.value > 0);
  }

  function ajustarHitosDePago(hitosPago, reserva) {
    const fechaActual = Date.now();
    let cantidadAcc = currency(0);
    let hitoAcumulado = false;
    let precioTotalSinTasasInternas = calculaPrecioTotalSinTasasInternas(reserva, false);
    let precioTotalSinTasasNiComplementos = precioTotalSinTasasInternas.subtract(reserva.precioComplementos);

    return hitosPago
      .map((hito, idx) => {
        const fechaLimite = new Date(hito.fecha);
        let cantidad = calculaCantidadHito(hito, precioTotalSinTasasNiComplementos);
        if (idx === 0) {
          cantidad = cantidad.add(reserva.precioComplementos);
        }
        cantidadAcc = cantidadAcc.add(cantidad);
        if (fechaActual > fechaLimite) {
          hitoAcumulado = true;
        } else {
          if (hitoAcumulado) {
            hitoAcumulado = false;
            return {
              cantidad: cantidadAcc,
              tipoCantidad: hito.tipoCantidad,
              fecha: hito.fecha,
            };
          } else {
            if (HITOS_PAGO_TIPOCANTIDAD.RESTANTE === hito.tipoCantidad) {
              cantidad = precioTotalSinTasasInternas.subtract(cantidadAcc);
            }
            return {
              cantidad: cantidad,
              tipoCantidad: hito.tipoCantidad,
              fecha: hito.fecha,
            };
          }
        }
      })
      .filter(hito => hito !== undefined);
  }

  function calculaCantidadHito(hitoPago, precioTotalSinTasasInternas) {
    if (HITOS_PAGO_TIPOCANTIDAD.FIJO === hitoPago.tipoCantidad) {
      return currency(hitoPago.cantidad);
    } else if (HITOS_PAGO_TIPOCANTIDAD.PORCENTAJE === hitoPago.tipoCantidad) {
      return currency(hitoPago.cantidad).divide(100).multiply(precioTotalSinTasasInternas);
    } else {
      return currency(0);
    }
  }

  function handlePagoUnicoChanges(selectedValue) {
    isPagoUnicoSelected.value = selectedValue;
    emits('pago-unico', selectedValue);
  }

  watch(hitosPago, hitosPago => {
    emits('precio-calendarizado', hitosPago[0]?.cantidad);
  });

  onMounted(async () => {
    await nextTick();
    emits('pago-unico', isPagoUnicoSelected.value);
    emits('precio-calendarizado', hitosPago.value[0]?.cantidad);
  });
</script>
